import { enrichBreadcrumbsWithDataHooks } from './enrichBreadcrumbsWithDataHooks';
import { calculateAppEnvironment } from './calculateAppEnvironment';
export function prepareOptions(options) {
    const { dsn, appName = '', version, environment, beforeSend, transport, integrations, artifactId, wixRecorder, } = options;
    return {
        dsn,
        environment: environment || calculateAppEnvironment(),
        ...(version && { release: `${artifactId ?? appName}@${version}` }),
        beforeBreadcrumb: enrichBreadcrumbsWithDataHooks,
        beforeSend: async (event, hint) => {
            let newEvent;
            const originalFingerprint = event.fingerprint;
            const isBeforeSendExists = !!beforeSend;
            if (isBeforeSendExists) {
                event.tags = { ...event.tags, customBeforeSend: true };
            }
            try {
                newEvent = isBeforeSendExists
                    ? (await beforeSend(event, hint))
                    : event;
            }
            catch (e) {
                // should add BI here!
                event.contexts = {
                    ...event.contexts,
                    errorMonitorBuiltInContext: {
                        ...event.contexts?.errorMonitorBuiltInContext,
                        customBeforeSendError: true,
                    },
                };
                newEvent = event;
            }
            if (!newEvent) {
                event.fingerprint = originalFingerprint;
                event.contexts = {
                    ...event.contexts,
                    errorMonitorBuiltInContext: {
                        ...event.contexts?.errorMonitorBuiltInContext,
                        customBeforeSendReturnNull: true,
                    },
                };
                event.environment = 'unknown';
                return event;
            }
            newEvent.fingerprint = originalFingerprint;
            const availableStacktraces = [
                ...(newEvent.exception?.values?.map((error) => error.stacktrace) ?? []),
                newEvent.stacktrace,
            ];
            const stackTraceArtifactIds = availableStacktraces.flatMap((stacktrace) => extractArtifactIdsFromStacktrace(stacktrace));
            // extracting artifactId from the url error
            // in case of a fetch call to an artifact parastorage link and it fails
            const urlArtifactId = extractArtifactIdFromParastoragePath([
                newEvent.contexts?.httpErrorDetails?.requestConfig
                    ?.url,
            ]);
            const detectedArtifactIds = Array.from(new Set([...stackTraceArtifactIds, ...urlArtifactId]));
            if (artifactId &&
                !detectedArtifactIds.includes(artifactId) &&
                newEvent.environment === 'production') {
                newEvent.environment = 'unknown';
            }
            newEvent.contexts = {
                ...newEvent.contexts,
                errorMonitorBuiltInContext: {
                    ...newEvent.contexts?.errorMonitorBuiltInContext,
                    detectedArtifactIds,
                },
            };
            const sessionRecordingUrl = await wixRecorder?.recordUrl();
            if (sessionRecordingUrl) {
                newEvent.tags = {
                    ...newEvent.tags,
                    sessionRecordingUrl,
                };
                if (sessionRecordingUrl.startsWith('https://app.fullstory.com')) {
                    /**
                     * Set fullStory url into the same context, where official "sentry-fullstory" integration is putting it
                     * Need to verify, but looks like this will help with enabling Fullstory integration from Sentry UI side
                     * https://github.com/getsentry/sentry-fullstory/blob/master/src/SentryFullStory.ts#L73
                     */
                    newEvent.contexts = {
                        ...newEvent.contexts,
                        fullStory: {
                            fullStoryUrl: sessionRecordingUrl,
                        },
                    };
                }
                else {
                    newEvent.contexts = {
                        ...newEvent.contexts,
                        errorMonitorBuiltInContext: {
                            ...newEvent.contexts?.errorMonitorBuiltInContext,
                            sessionRecordingUrl,
                        },
                    };
                }
            }
            return newEvent;
        },
        transport,
        integrations,
    };
}
const extractArtifactIdsFromStacktrace = (stacktrace) => {
    const frames = stacktrace?.frames ?? [];
    const allFileNamesFromStack = frames
        .map((frame) => frame.abs_path || frame.filename)
        .filter((path) => !!path)
        .reverse();
    return extractArtifactIdFromParastoragePath(allFileNamesFromStack);
};
const extractArtifactIdFromParastoragePath = (paths) => {
    const parastorageRegex = /(?:\/services|\/_partials)\/([^/]*)\/((?:0|[1-9]\d*)\.(?:0|[1-9]\d*)\.(?:0|[1-9]\d*)|[a-f0-9]{56})\//i;
    return paths
        ?.map((fileName) => parastorageRegex.exec(fileName ?? ''))
        .filter((matches) => !!matches)
        .map((matches) => matches[1]);
};
//# sourceMappingURL=prepareOptions.js.map